import React, { type FC } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Button, TextField } from '@mui/material'
import AddOutlined from '@mui/icons-material/AddOutlined'
import { WizardHeader } from '@/common/components'
import { extractProjectName } from '@/common/utils'

interface SessionNameStepProps {
  readonly initialValue: string
  readonly projectKey: string
  readonly onNext: (sessionName: string) => void
  readonly onCancel: () => void
  readonly onBack?: () => void
}

export const SessionNameStep: FC<SessionNameStepProps> = ({
  initialValue,
  projectKey,
  onNext,
  onCancel,
  onBack,
}) => {
  const [name, setName] = useState(initialValue)
  const nameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    nameRef.current?.focus()
  }, [])

  const trimmedName = name.trim()
  const isValid = trimmedName.length > 0
  const projectDisplayName = extractProjectName(projectKey)

  const handleSubmit = useCallback(() => {
    if (isValid) {
      onNext(trimmedName)
    }
  }, [isValid, trimmedName, onNext])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && isValid) {
        handleSubmit()
      }
    },
    [isValid, handleSubmit],
  )

  return (
    <Box sx={containerSx}>
      <WizardHeader
        icon={<AddOutlined sx={iconSx} />}
        title="Start New Session"
        description={
          <>
            Launch a new Claude Code session in the{' '}
            <Box component="span" sx={projectNameSx}>
              {projectDisplayName}
            </Box>{' '}
            project
          </>
        }
      />

      <TextField
        label="Session Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={handleKeyDown}
        fullWidth
        inputRef={nameRef}
        sx={fieldSx}
      />

      <Box sx={actionsSx}>
        {onBack ? (
          <Button onClick={onBack}>Back</Button>
        ) : (
          <Button onClick={onCancel}>Cancel</Button>
        )}
        <Button variant="contained" onClick={handleSubmit} disabled={!isValid}>
          Next
        </Button>
      </Box>
    </Box>
  )
}

const iconSx: SxProps<Theme> = {
  color: 'info.main',
}

const projectNameSx: SxProps<Theme> = {
  color: 'info.main',
  fontWeight: 600,
}

const containerSx: SxProps<Theme> = {
  py: 2,
}

const fieldSx: SxProps<Theme> = {
  mb: 3,
}

const actionsSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  mt: 4,
}
